import { Ban } from 'lucide-preact'
import type { SlotView } from '#contract'
import { useT } from '../i18n/index.ts'
import styles from './slot.module.css'

interface SlotProps {
  readonly slot: SlotView
  /** The key that chooses it, top left: QuickAccess's 1–9. */
  readonly number?: number
  /** The slot in use: accent border. */
  readonly active?: boolean
  /** It won't take what the cursor holds: dimmed, with a ban mark. */
  readonly refused?: boolean
  /** A changed count pops the icon up instead of dipping it (a stack put down). */
  readonly pop?: boolean
}

/**
 * One inventory slot: the item's icon (or its name, if the icon failed), its count when
 * more than one. The icon dips when the count changes: one eaten, one more picked up.
 */
export function Slot({ slot, number, active = false, refused = false, pop = false }: SlotProps) {
  const t = useT()
  const name = slot ? t(slot.item.name) : ''
  return (
    <div
      class={styles.slot}
      data-active={active || undefined}
      data-refused={refused || undefined}
      data-pop={pop || undefined}
      // The inventory screen has its own tooltip.
      title={(!pop && name) || undefined}
    >
      {refused && <Ban class={styles.ban} aria-hidden="true" />}
      {number !== undefined && <span class={styles.number}>{number}</span>}
      {slot &&
        (slot.item.icon ? (
          <img
            key={slot.count}
            class={styles.icon}
            src={slot.item.icon}
            alt={name}
            draggable={false}
          />
        ) : (
          <span key={slot.count} class={styles.name}>
            {name}
          </span>
        ))}
      {slot && slot.count > 1 && <span class={styles.count}>{slot.count}</span>}
    </div>
  )
}
